import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import PageMeta from '../../../components/PageMeta';
import {
  executeManualCustomerClaim,
  previewManualCustomerClaim,
} from '../../../services/marketing';

const stageColorMap = {
  unassigned: 'warning',
  claimed: 'success',
  auto_assigned: 'info',
  admin_assigned: 'secondary',
};

const actionColorMap = {
  create_assignment: 'primary',
  assign_unassigned: 'primary',
  assign_expired_unassigned: 'warning',
  confirm_auto_assigned_manager: 'success',
  override_auto_assigned_manager: 'warning',
  reassign_existing: 'secondary',
  already_assigned: 'success',
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const formatLabel = (value) => {
  if (!value) return 'N/A';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const InfoRow = ({ label, value }) => (
  <Box mb={2}>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{value || 'N/A'}</Typography>
  </Box>
);

const AdminManualClaim = () => {
  const [marketerQuery, setMarketerQuery] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showNotification = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadPreview = async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    if (!marketerQuery.trim() || !customerQuery.trim()) {
      setError('Enter both marketer and customer identifiers before searching.');
      return;
    }

    setLoading(true);
    setError('');
    setPreview(null);

    try {
      const data = await previewManualCustomerClaim(marketerQuery.trim(), customerQuery.trim());
      setPreview(data);
    } catch (requestError) {
      const responseData = requestError.response?.data;
      setPreview(responseData || null);
      setError(responseData?.error || responseData?.decision?.message || 'Failed to fetch manual claim preview.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!preview?.decision?.allowed) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await executeManualCustomerClaim({
        marketer_query: marketerQuery.trim(),
        customer_query: customerQuery.trim(),
        notes: notes.trim(),
      });
      setPreview(response);
      showNotification(response.message || 'Customer assignment updated successfully.', 'success');
    } catch (requestError) {
      const responseData = requestError.response?.data;
      if (responseData) {
        setPreview(responseData);
      }
      setError(responseData?.error || responseData?.decision?.message || 'Failed to apply manual claim.');
      showNotification(responseData?.error || 'Failed to apply manual claim.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const marketer = preview?.marketer;
  const customer = preview?.customer;
  const assignment = preview?.assignment;
  const decision = preview?.decision;
  const currentManager = assignment?.current_manager;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden',
      }}
    >
      <PageMeta title="Admin Manual Claim | Marketing" />

      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Admin Manual Claim
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Search a marketer and customer, review the current assignment state, then assign or reassign the customer manually.
        </Typography>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <form onSubmit={loadPreview}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Marketer Identifier"
                  value={marketerQuery}
                  onChange={(event) => setMarketerQuery(event.target.value)}
                  placeholder="Username, email, or phone"
                  disabled={loading || submitting}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Customer Identifier"
                  value={customerQuery}
                  onChange={(event) => setCustomerQuery(event.target.value)}
                  placeholder="Username, email, or phone"
                  disabled={loading || submitting}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Admin Notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Optional note explaining why this manual assignment is needed"
                  disabled={loading || submitting}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  disabled={loading || submitting || !marketerQuery.trim() || !customerQuery.trim()}
                  sx={{ height: 56 }}
                >
                  {loading ? 'Searching...' : 'Fetch Preview'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {!!error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {preview && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Marketer Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {marketer ? (
                  <>
                    <InfoRow label="Full Name" value={marketer.full_name} />
                    <InfoRow label="Employee ID" value={marketer.employee_id} />
                    <InfoRow label="Username" value={marketer.user?.username} />
                    <InfoRow label="Email" value={marketer.user?.email} />
                    <InfoRow label="Phone" value={marketer.user?.phone} />
                    <InfoRow label="Status" value={formatLabel(marketer.status)} />
                    <InfoRow
                      label="Capacity"
                      value={`${marketer.current_customer_count}/${marketer.max_customers} customers (${marketer.available_slots} slots left)`}
                    />
                    <InfoRow
                      label="Auto Assignment Availability"
                      value={marketer.is_available_for_auto_assignment ? 'Available' : 'Unavailable'}
                    />
                  </>
                ) : (
                  <Alert severity="warning">Marketer was not found.</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {customer ? (
                  <>
                    <InfoRow label="Full Name" value={customer.fullname} />
                    <InfoRow label="Username" value={customer.username} />
                    <InfoRow label="Email" value={customer.email} />
                    <InfoRow label="Phone" value={customer.phone} />
                    <InfoRow label="Joined" value={formatDateTime(customer.date_joined)} />
                    <InfoRow label="Customer SID" value={customer.sid} />
                  </>
                ) : (
                  <Alert severity="warning">Customer was not found.</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Assignment Review
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {assignment ? (
                  <>
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Current Stage
                      </Typography>
                      <Chip
                        sx={{ mt: 0.5 }}
                        label={formatLabel(assignment.stage)}
                        color={stageColorMap[assignment.stage] || 'default'}
                        size="small"
                      />
                    </Box>

                    <InfoRow label="Assigned At" value={formatDateTime(assignment.assigned_at)} />
                    <InfoRow label="Assigned By" value={assignment.assigned_by_username} />
                    <InfoRow label="Auto Assign After" value={formatDateTime(assignment.auto_assign_after)} />
                    <InfoRow
                      label="Claim Window"
                      value={assignment.claim_window_expired ? 'Expired' : 'Active or not applicable'}
                    />
                    <InfoRow label="Reassignment Count" value={String(assignment.reassignment_count ?? 0)} />
                    <InfoRow label="Internal Notes" value={assignment.notes} />

                    {currentManager && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Current manager: {currentManager.full_name} ({currentManager.user?.username || 'N/A'})
                      </Alert>
                    )}
                  </>
                ) : (
                  <Alert severity="info">
                    No assignment record exists yet for this customer. A new manual assignment can be created if the customer is eligible.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Action Preview
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {decision && (
                  <>
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Recommended Action
                      </Typography>
                      <Chip
                        sx={{ mt: 0.5 }}
                        label={formatLabel(decision.action)}
                        color={actionColorMap[decision.action] || 'default'}
                        size="small"
                      />
                    </Box>

                    <InfoRow label="Decision" value={decision.title} />
                    <InfoRow label="Summary" value={decision.message} />

                    {!!decision.reasons?.length && (
                      <Alert severity={decision.allowed ? 'info' : 'warning'} sx={{ mb: 3 }}>
                        {decision.reasons.join(' ')}
                      </Alert>
                    )}

                    <Button
                      fullWidth
                      variant="contained"
                      color={decision.allowed ? 'primary' : 'inherit'}
                      startIcon={
                        submitting ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : decision.allowed ? (
                          <AssignmentTurnedInIcon />
                        ) : (
                          <CheckCircleIcon />
                        )
                      }
                      onClick={handleSubmit}
                      disabled={!decision.allowed || submitting}
                      sx={{ height: 56 }}
                    >
                      {submitting ? 'Applying...' : decision.submit_label || 'Apply Manual Claim'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminManualClaim;
